import { DateRange as DateRangeIcon } from "@mui/icons-material";
import { Box, Button, Divider } from "@mui/material";
import dayjs from "dayjs";
import React, { FunctionComponent, memo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DateRange } from "types/global";
import { humanDateRange, todayDateRange, haveValueDateRange, oneDayDateRange } from "utils";
import TextField from "../fields/TextField";
import Modal from "../Modal";
import Chip from "./Chip";
import MultiDaysCalendar from "./MultiDaysCalendar";



interface Props {
	dateRange: DateRange
	defaultDateRange?: DateRange
	onChange?: (dateRange: DateRange) => void
	sx?: any
}

/**
 * picker per una range di tipo DateRange
 */
const DateRangePickerH: FunctionComponent<Props> = ({
	dateRange,
	defaultDateRange,
	onChange,
	sx,
}) => {

	// HOOKs
	const { t } = useTranslation()
	const [isOpen, setIsOpen] = useState<boolean>(false)
	const [dateRangeSel, setDateRangeSel] = useState<DateRange>({})

	// HANDLERs
	const handleCancel = () => {
		setIsOpen(false)
	}
	const handleSelect = () => {
		setIsOpen(false)
		onChange?.(dateRangeSel)
	}
	const handleToday = () => {
		setDateRangeSel(todayDateRange())
	}

	const handleOpen = () => {
		setDateRangeSel(dateRange)
		setIsOpen(true)
	}
	const handleChange = (range: DateRange) => {
		setDateRangeSel(range)
	}
	const handleClear = (e: React.MouseEvent) => {
		e.stopPropagation()
		setIsOpen(false)
		onChange?.(null)
	}

	const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const startDate = e.target.value ? new Date(e.target.value).getTime() : undefined;
		setDateRangeSel({ ...dateRangeSel, start: startDate });
	};

	const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const endDate = e.target.value ? new Date(e.target.value).getTime() : undefined;
		setDateRangeSel({ ...dateRangeSel, end: endDate })
	};

	// Helper functions to format dates for input fields
	const formatDateForInput = (timestamp?: number): string => {
		if (!timestamp) return '';
		return dayjs(timestamp).format('YYYY-MM-DD');
	};

	// RENDER 
	const dateRangeStr = humanDateRange(haveValueDateRange(dateRange) ? dateRange : defaultDateRange) ?? "EVERY DAY"
	const haveValue = haveValueDateRange(dateRange) //&& !isSingleDate(dateRange)

	return (<>
		<Chip sx={sx}
			label={dateRangeStr}
			icon={<DateRangeIcon sx={{ width: "18px", height: "18px" }} />}
			tooltip={t("selector.range-date.tooltip")}
			active={haveValue}
			onClick={handleOpen}
			onClear={handleClear}
		/>
		<Modal
			sx={{ ".MuiDialog-paper": { width: "400px" } }}
			title="DATE RANGE"
			icon={<DateRangeIcon />}
			maxWidth="xs"

			isOpen={isOpen}
			onClose={handleCancel}
			actionsRender={<>
				<Button
					onClick={handleToday}
				>TODAY</Button>
				<Box sx={{ flex: "1 1 auto" }} />
				<Button onClick={handleCancel}>
					{t("dialog.default.cancel")}
				</Button>
				<Button onClick={handleSelect} color="secondary">
					Select
				</Button>
			</>}
		>

			<Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 2 }}>
				<Box sx={{ fontSize: "12px", }}>START</Box>
				<TextField sx={{ width: "120px" }}
					type="date"
					value={formatDateForInput(dateRangeSel.start)}
					onChange={handleStartDateChange}
				/>
				<Box sx={{ flex: 1 }} />
				<Box sx={{ fontSize: "12px", }}>END</Box>
				<TextField sx={{ width: "120px" }}
					type="date"
					value={formatDateForInput(dateRangeSel.end)}
					onChange={handleEndDateChange}
				/>
			</Box>

			<Divider sx={{ mt: 2, mb: 1 }} />

			<MultiDaysCalendar
				dateRange={dateRangeSel}
				onChange={handleChange}
			/>

		</Modal>
	</>)
}

export default memo(DateRangePickerH, (old: Props, current: Props) => old.dateRange === current.dateRange)
